import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomStepper from './CustomStepper';
import { StepperContainer } from './StepProgressBar.styled';
import { setProgressBarCurrentStep } from '../../../pages/scheduleSurgery/redux/actions';
import { setProgressBarCurrentStep as setEquityProgressBarCurrentStep } from '../../../pages/equity/redux/actions';
import { setProgressBarCurrentStep as setConsignmentAddProgressBarCurrentStep } from '../../../pages/consignmentAdd/redux/actions';
import { setProgressBarCurrentStep as setConsignmentProgressBarCurrentStep } from '../../../pages/consignmentBilling/redux/actions';
import { setProgressBarCurrentStep as setConsignmentReturnProgressBarCurrentStep } from '../../../pages/consignmentReturn/redux/actions';
import { sizes } from '../../../styles/Media.styled';
import {
    SCHEDULESURGERYSTEPS,
    ORDEREQUITYSTEPS,
    EQUITYCATEGORY,
    CONSIGNMENT_ADD_CATEGORY,
    ORDER_CONSIGNMENT_ADD_STEPS,
    CONSIGNMENTCATEGORY,
    CONSIGNMENTBILLINGPROGRESSTEPS,
    CONSIGNMENTRETURNCATEGORY,
    CONSIGNMENTRETURNPROGRESSTEPS,
} from '../../../constants/productConstants';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';

export default function StepProgressBar({
    steps,
    currentActivity,
    stepValidation = false,
    category,
    sameBatchAllocationError = false,
    height,
    progressColor,
    stepperBackgroundColor,
    progressBarIconClickHandler,
    procedurePageVisibility,
    countryCode = countryCode,
    replenishmentPageVisibilityUsage,
    isFromReportUsage = false,
}) {
    const dispatch = useDispatch();
    const isCondensedView = useSelector(
        (state) => state.loginReducer.isCondensedView
    );
    const { isLanguageChanged, countryISOCode } = useSelector((state) => state.loginReducer);
    const isMobileDevice = window.innerWidth <= sizes.mobileDevice;
    
    const getCategory = (category) => {
        switch (category) {
            case EQUITYCATEGORY:
                return ORDEREQUITYSTEPS;
            case CONSIGNMENT_ADD_CATEGORY:
                return ORDER_CONSIGNMENT_ADD_STEPS;
            case CONSIGNMENTCATEGORY:
                return CONSIGNMENTBILLINGPROGRESSTEPS;
            case CONSIGNMENTRETURNCATEGORY:
                return CONSIGNMENTRETURNPROGRESSTEPS;
            default:
                return SCHEDULESURGERYSTEPS;
        }
    };
    
    // Replace Account by 'Acct' and Payment by 'Pay' in mobile UI (not for Japanese users)
    const mappedSteps = React.useMemo(() => {
        if (category !== CONSIGNMENTCATEGORY || !isMobileDevice || isLanguageChanged || countryISOCode === 'JP') return steps;
       
        return {
            ...steps,
            ...(steps.Account && { Account: { ...steps.Account, displayLabel: 'Acct' } }),
            ...(steps.Payment && { Payment: { ...steps.Payment, displayLabel: 'Pay' } })
        };
    }, [steps, isMobileDevice, category, isLanguageChanged, countryISOCode]);
    
    useEffectWithErrorLogging(() => {
        document.documentElement.style.setProperty(
            '--label-color',
            isCondensedView ? '#3c3c3c' : '#707070'
        );
    }, [isCondensedView]);
    !procedurePageVisibility ? delete mappedSteps.Procedure : null;
    !replenishmentPageVisibilityUsage ? delete mappedSteps.Replen : null;
    const handleClick = (step) => {
        const indexOfCurrentActivity = getCategory().findIndex(
            (activity) => activity === currentActivity
        );
        const indexOfNextActivity = getCategory().findIndex(
            (activity) => activity === step
        );

        // Block navigation only when trying to move forward with invalid current step or batch allocation error
        if (
            (indexOfNextActivity > indexOfCurrentActivity && stepValidation) ||
            sameBatchAllocationError
        ) { 
            return;
        }

        // Allow navigation to any step that is already completed or the Details step
        if (mappedSteps[step].done || step === 'Details' || indexOfNextActivity <= indexOfCurrentActivity) { 
            
            // Dispatch the appropriate action based on category
            switch (category) {
                case EQUITYCATEGORY:
                    dispatch(setEquityProgressBarCurrentStep(step));
                    break;
                case CONSIGNMENT_ADD_CATEGORY:
                    dispatch(setConsignmentAddProgressBarCurrentStep(step));
                    break;
                case CONSIGNMENTCATEGORY:
                    dispatch(setConsignmentProgressBarCurrentStep(step));
                    break;
                case CONSIGNMENTRETURNCATEGORY: 
                    dispatch(setConsignmentReturnProgressBarCurrentStep(step));
                    break;
                default:
                    dispatch(setProgressBarCurrentStep(step));
            }
        }
    };  
    return (
        <>
            <StepperContainer
                $marginleftdesk={isCondensedView ? '160px' : '0px'}
                $marginrightdesk={isCondensedView ? '160px' : '0px'}
                height={height}
                $mbMarginRight={isCondensedView ? '-15px' : '0px'}
                $mbMarginLeft={isCondensedView ? '-15px' : '0px'}
                id='orders-progress-bar'
            >
                <CustomStepper
                    steps={mappedSteps}
                    handleClick={isFromReportUsage ? progressBarIconClickHandler : handleClick}
                    sameBatchAllocationError={sameBatchAllocationError}
                    progressColor={progressColor}
                    stepperBackgroundColor={stepperBackgroundColor}
                    isFromReportUsage={isFromReportUsage}
                />
            </StepperContainer>
        </>
    );
}
